import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchMe, login as loginApi, logout as logoutApi } from '../api/auth';
import type { User } from '../api/types';

const ME_KEY = ['auth', 'me'] as const;

// Centralt auth-hook. React Query agerar single source of truth för "vem är
// inloggad" — vi cache:ar `me` och invaliderar vid login/logout.
export function useAuth() {
  const queryClient = useQueryClient();

  const meQuery = useQuery<User | null>({
    queryKey: ME_KEY,
    queryFn: fetchMe,
    // Inget polling — sessionen ändras bara via login/logout.
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const loginMutation = useMutation({
    mutationFn: (pin: string) => loginApi(pin),
    onSuccess: (user) => {
      queryClient.setQueryData(ME_KEY, user);
    },
  });

  const logoutMutation = useMutation({
    mutationFn: () => logoutApi(),
    onSuccess: () => {
      queryClient.setQueryData(ME_KEY, null);
      // Rensa allt cachat — annars riskerar vi att Henrik ser Michelles data
      // efter user-byte.
      queryClient.clear();
    },
  });

  return {
    user: meQuery.data ?? null,
    isLoading: meQuery.isLoading,
    isAuthenticated: !!meQuery.data,
    login: loginMutation.mutateAsync,
    loginState: {
      isPending: loginMutation.isPending,
      error: loginMutation.error,
      reset: loginMutation.reset,
    },
    logout: logoutMutation.mutateAsync,
  };
}
